import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { DesignData, MediaText } from 'editor/src/store/design/types';
import applyValueFormat from 'editor/src/store/design/util/applyValueFormat';
import buildSharedTextValueUpdate from 'editor/src/store/design/util/buildSharedTextValueUpdate';
import { getFieldKeyMembers } from 'editor/src/store/design/util/fieldKeys';
import {
  getSharedTextFieldMembers,
  receivesSharedValue,
  SharedTextFieldMember,
} from 'editor/src/store/design/util/sharedTextFields';
import isSampleFlagChangeAllowed from 'editor/src/store/editor/selector/isSampleFlagChangeAllowed';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { Thunk } from 'editor/src/store/hooks';

import syncSharedTextValueOperation from './syncSharedTextValueOperation';
import updateMediaElementOperation, { MediaUpdateActionName } from './updateMediaElementOperation';

export interface PersonalizationFieldValue {
  /** The field key the author gave the text (`extra.fieldKey`), or the
   *  `sharedFieldId` of a linked group. */
  fieldId: string;
  value: string;
}

export const SET_FIELD_VALUES_UNDO_KEY = 'Field values set by host';

/**
 * Fills personalisable texts with values the host already knows — a name, a
 * date, a venue taken from the host's own record — so the buyer starts from
 * their details instead of the template's sample text.
 *
 * A field is addressed by the key the template author gave it (`fieldKey`, e.g.
 * `deceased_name`), or by the `sharedFieldId` that ties the front's Name to the
 * back's. A keyed text that is linked fills its whole group; texts carrying the
 * same key without being linked are each filled. Either way the value is written
 * through the same propagation a buyer's keystroke goes through, so a prefilled
 * card stays consistent on both sides and the template's value format (mask,
 * forced domain) is applied exactly as it would be to typed input.
 *
 * Anything the operation cannot place is ignored rather than reported as an
 * error: an id the design does not carry (the host's record is generic, the
 * template uses a subset), a blank value (nothing to say — the sample stays),
 * or a group with no member that may receive a value (locked, per-option, or
 * code-set bound — see receivesSharedValue).
 *
 * Every write lands in one batch behind one undo entry, so a single undo puts
 * the template's text back.
 *
 * Returns the field ids that were actually written.
 */
const setPersonalizationFieldValuesOperation =
  (fieldValues: unknown): Thunk =>
  (dispatch, getState): string[] => {
    const values = normalizeFieldValues(fieldValues);
    const state = getState();
    const { designData } = state.design;
    if (!designData || !values.size) {
      return [];
    }

    const sampleFlagChangeAllowed = isSampleFlagChangeAllowed(state);
    // Shared across ids: a key on a linked member and the group's own
    // sharedFieldId reach the same group, which is written once, by the first
    // entry that reaches it.
    const seenGroups = new Set<string>();
    const writes = [...values].flatMap(([fieldId, value]) =>
      resolveFieldTargets(designData, fieldId, seenGroups).flatMap(({ source, members, sharedFieldId }) => {
        const text = applyValueFormat(value, source.element.extra.valueFormat);
        // Nothing to do when every member already shows this value as real
        // content. Checked over the whole group, not only the source, so a group
        // that drifted apart is still brought back together. A member still
        // flagged as sample counts as unfilled even when its placeholder happens
        // to read the same: the buyer's panel shows a sample as an empty input.
        if (
          members.every(({ element }) => element.extra.text === text && (sampleFlagChangeAllowed || !element.sample))
        ) {
          return [];
        }
        return [{ fieldId, value, text, source, sharedFieldId }];
      }),
    );

    if (!writes.length) {
      return [];
    }

    batch(() => {
      dispatch(saveUndoRedoStateOperation(SET_FIELD_VALUES_UNDO_KEY));

      writes.forEach(({ value, text, source, sharedFieldId }) => {
        const { element, address } = source;
        if (element.extra.text !== text) {
          // Through the same operation the buyer's typing goes through: it
          // pours the value through the template's format, clears the sample
          // flag and a custom font's stale outline, re-packs a reflow row, and
          // mirrors the result onto every other member of the group.
          dispatch(
            updateMediaElementOperation(
              address,
              buildSharedTextValueUpdate(element, value, sampleFlagChangeAllowed, element.extra.valueFormat),
              MediaUpdateActionName.TEXT_UPDATED,
              true,
            ),
          );
        } else if (sharedFieldId) {
          // The source already holds the value, so the operation above would
          // see no change and mirror nothing. A member that drifted still has
          // to receive it.
          dispatch(
            syncSharedTextValueOperation(element.uuid, sharedFieldId, {
              text,
              valueFormat: element.extra.valueFormat,
            }),
          );
        }
      });

      // The writes above clear the sample flag only on members whose text
      // moved. A member that already read the host's value is now real
      // content too, so it stops being a sample.
      if (!sampleFlagChangeAllowed) {
        writes.forEach(({ source, sharedFieldId }) => {
          const { designData: current } = getState().design;
          const members = sharedFieldId
            ? getSharedTextFieldMembers(current, sharedFieldId)
            : [{ element: elementAt(current, source), address: source.address }];
          members.forEach(({ element, address }) => {
            if (element?.sample && receivesSharedValue(element)) {
              dispatch(updateMediaElementAction({ elementAddress: address, elementUpdate: { sample: undefined } }));
            }
          });
        });
      }
    });

    return [...new Set(writes.map(({ fieldId }) => fieldId))];
  };

interface FieldTarget {
  /** The member the value is written through. */
  source: SharedTextFieldMember;
  /** Every text that ends up holding the value. */
  members: SharedTextFieldMember[];
  /** Set when the target is a linked group, whose members follow the source. */
  sharedFieldId?: string;
}

/**
 * Where a value for `fieldId` goes: every text carrying it as `fieldKey`, a
 * linked one standing for its whole group, then the group whose `sharedFieldId`
 * it is. A group is written once however many of its members carry the key,
 * and not again for a later id that reaches it (`seenGroups`).
 * Members that may not receive a value (locked, per-option, code-set bound) are
 * left out; a target with none left is dropped.
 */
function resolveFieldTargets(designData: DesignData, fieldId: string, seenGroups: Set<string>): FieldTarget[] {
  const targets: FieldTarget[] = [];
  const addGroup = (sharedFieldId: string) => {
    if (seenGroups.has(sharedFieldId)) {
      return;
    }
    seenGroups.add(sharedFieldId);
    const members = getSharedTextFieldMembers(designData, sharedFieldId).filter(({ element }) =>
      receivesSharedValue(element),
    );
    // The first member in design order is the source: the group holds one
    // format, so whichever member the value is poured through, it comes out
    // the same — and the rest receive it verbatim from the propagation.
    if (members.length) {
      targets.push({ source: members[0], members, sharedFieldId });
    }
  };

  getFieldKeyMembers(designData, fieldId).forEach((member) => {
    if (!receivesSharedValue(member.element)) {
      return;
    }
    const { sharedFieldId } = member.element.extra;
    if (sharedFieldId) {
      addGroup(sharedFieldId);
    } else {
      targets.push({ source: member, members: [member] });
    }
  });
  addGroup(fieldId);

  return targets;
}

function elementAt(designData: DesignData | undefined, { address }: SharedTextFieldMember): MediaText | undefined {
  const element =
    designData?.spreads[address.spreadIndex]?.pages[address.pageIndex]?.groups.media?.[address.elementIndex];
  return element?.type === 'text' ? element : undefined;
}

/**
 * The payload as posted, reduced to the entries that can mean something: a
 * string id and a non-blank string value, trimmed. Later entries for the same id win,
 * the way a host patching its own list would expect.
 */
function normalizeFieldValues(fieldValues: unknown): Map<string, string> {
  const values = new Map<string, string>();
  if (!Array.isArray(fieldValues)) {
    return values;
  }

  fieldValues.forEach((entry) => {
    if (
      entry &&
      typeof entry === 'object' &&
      typeof entry.fieldId === 'string' &&
      entry.fieldId &&
      typeof entry.value === 'string' &&
      entry.value.trim()
    ) {
      // Trimmed: padding from the host's record is not part of the value, and
      // it would print as offset text and count against the character cap.
      values.set(entry.fieldId, entry.value.trim());
    }
  });

  return values;
}

export default setPersonalizationFieldValuesOperation;
